// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Disposal Sites CRUD
// TDSR, DMS, final disposal, recycler management
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Autocomplete,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, MultiSelect, Switch,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh, IconChevronDown, IconChevronRight, IconDownload,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { exportCSV } from '../../lib/exportCSV';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useAllTickets } from '../../hooks/useAllTickets';
import { useModulePermission } from '../../hooks/useModulePermission';
import { DEBRIS_TYPE_LABELS } from '../../types/debris';
import type { DebrisDisposalSite, SiteStatus } from '../../types/debris';
import { WORKER_URL, TABLES, FIELDS } from '../../lib/api-switch';
import { queryRecords } from '../../api/client';

// ── DDMS Notes Meta encode/decode ──
interface DDMSMeta {
  depId?: string;
  depContactName?: string;
  depContactEmail?: string;
  depContactPhone?: string;
  putrescibleRule?: boolean;
  spotterRequired?: boolean;
  threshold?: number;
}
const DDMS_META_RE = /\[DDMS:([\s\S]*?)\]/;

function decodeDDMSMeta(raw: string): { notes: string; ddms: DDMSMeta } {
  if (!raw) return { notes: '', ddms: {} };
  const match = raw.match(DDMS_META_RE);
  if (!match) return { notes: raw, ddms: {} };
  const clean = raw.replace(DDMS_META_RE, '').trim();
  try { return { notes: clean, ddms: JSON.parse(match[1]) }; }
  catch { return { notes: clean, ddms: {} }; }
}

function encodeDDMSMeta(userNotes: string, ddms: DDMSMeta): string {
  const clean = (userNotes || '').replace(DDMS_META_RE, '').trim();
  const nonEmpty = Object.fromEntries(
    Object.entries(ddms).filter(([, v]) => v !== '' && v !== undefined && v !== null),
  );
  if (Object.keys(nonEmpty).length === 0) return clean;
  const metaStr = JSON.stringify(nonEmpty);
  return clean ? `[DDMS:${metaStr}]\n${clean}` : `[DDMS:${metaStr}]`;
}

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'full', label: 'Full' },
  { value: 'closed', label: 'Closed' },
];

const SITE_TYPES = [
  { value: 'tdsr', label: 'TDSR (Temporary Staging)' },
  { value: 'dms', label: 'DMS (Debris Management)' },
  { value: 'final_disposal', label: 'Final Disposal' },
  { value: 'recycler', label: 'Recycler' },
];

const STATUS_COLORS: Record<string, string> = {
  open: 'var(--color-success)', full: '#F86A0B', closed: '#939598',
};

// Short labels for table display
const SITE_TYPE_SHORT: Record<string, string> = {
  tdsr: 'TDSR', dms: 'DMS', final_disposal: 'Final Disposal', recycler: 'Recycler',
};

const DEBRIS_TYPE_OPTIONS = Object.entries(DEBRIS_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

const EMPTY: Partial<DebrisDisposalSite> = {
  siteName: '', siteType: '', address: '', latitude: '', longitude: '',
  capacityCubicYards: '', currentVolume: '0', acceptedTypes: '[]',
  operatingHours: '', monitorName: '', monitorEmail: '',
  status: 'open', notes: '',
};

export default function DisposalSites() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    disposalSites, loadingSites,
    loadDisposalSites, createDisposalSite, updateDisposalSite, deleteDisposalSite,
  } = useDebrisStore();

  const { tickets } = useAllTickets();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisDisposalSite | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisDisposalSite | null>(null);
  const [form, setForm] = useState<Partial<DebrisDisposalSite>>({ ...EMPTY });
  const [formTypes, setFormTypes] = useState<string[]>([]);
  const [formDDMS, setFormDDMS] = useState<DDMSMeta>({});
  const [depSectionOpen, setDepSectionOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hoverRow, setHoverRow] = useState<number | null>(null);

  // Directory facilities for pre-fill
  const [dirFacilities, setDirFacilities] = useState<Array<{ value: string; label: string; street: string; city: string; state: string; zip: string; lat: string; lng: string }>>([]);
  const [dirFacilityPick, setDirFacilityPick] = useState<string | null>(null);

  useEffect(() => {
    if (!TABLES.facilities) return;
    const FF = FIELDS.facilities;
    queryRecords(TABLES.facilities, { limit: 200 }).then(res => {
      const DISPOSAL_TYPES = new Set(['disposal-site', 'debris-site']);
      setDirFacilities(res.records.filter((r: any) => DISPOSAL_TYPES.has(String(r[FF.type] ?? ''))).map((r: any) => ({
        value: String(r.record_id),
        label: String(r[FF.name] ?? `Facility #${r.record_id}`),
        street: String(r[FF.street] ?? ''),
        city: String(r[FF.city] ?? ''),
        state: String(r[FF.state] ?? ''),
        zip: String(r[FF.zip] ?? ''),
        lat: String(r[FF.latitude] ?? ''),
        lng: String(r[FF.longitude] ?? ''),
      })));
    }).catch(() => {});
  }, []);

  // Address autocomplete
  const [addrSuggestions, setAddrSuggestions] = useState<{ value: string; lat: string; lng: string }[]>([]);
  const addrTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAddrSearch = (q: string) => {
    setField('address', q);
    if (addrTimeout.current) clearTimeout(addrTimeout.current);
    if (q.length < 3) { setAddrSuggestions([]); return; }
    addrTimeout.current = setTimeout(async () => {
      try {
        const r = await fetch(`${WORKER_URL}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`, {
          headers: { 'Origin': 'https://apps.opengov.com' },
        });
        if (!r.ok) return;
        const data = await r.json();
        setAddrSuggestions((data.items || []).filter((i: any) => i.position).map((i: any) => ({
          value: i.title || i.address?.label || q,
          lat: String(i.position.lat), lng: String(i.position.lng),
        })));
      } catch { /* ignore */ }
    }, 300);
  };

  useEffect(() => { loadDisposalSites(); }, [loadDisposalSites]);

  const iSites = incidentId ? disposalSites : [];

  // Decode DDMS meta from notes for each site
  const sitesWithDDMS = useMemo(() =>
    iSites.map(s => {
      const { ddms } = decodeDDMSMeta(s.notes);
      return { ...s, ddms };
    }),
  [iSites]);

  // Build monitor dropdown from existing sites
  const knownMonitors = useMemo(() => {
    const map = new Map<string, { name: string; email: string }>();
    disposalSites.forEach(s => {
      if (s.monitorName?.trim()) map.set(s.monitorName, { name: s.monitorName, email: s.monitorEmail || '' });
    });
    return [...map.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [disposalSites]);
  const monitorSelectData = useMemo(() => knownMonitors.map(m => ({ value: m.name, label: m.name })), [knownMonitors]);

  // Compute actual delivered volume per site from ticket data
  const computedVolumeMap = useMemo(() => {
    const map = new Map<number, number>(); // siteId → total CY
    // Build lowercase name → siteId lookup for fuzzy matching
    const nameToId = new Map<string, number>();
    disposalSites.forEach(s => {
      if (s.siteName) nameToId.set(s.siteName.toLowerCase(), s.id);
    });

    tickets.forEach((t: any) => {
      let siteId: number | null = null;
      // 1. FK match
      if (t.disposalSiteId && Number(t.disposalSiteId) > 0) {
        siteId = Number(t.disposalSiteId);
      } else if (t.disposalSiteName) {
        // 2. Fuzzy name match: ticket name contains DB name OR DB name contains ticket name
        const tl = t.disposalSiteName.toLowerCase();
        for (const [dbName, id] of nameToId) {
          if (tl.includes(dbName) || dbName.includes(tl)) { siteId = id; break; }
        }
      }
      if (siteId) {
        const cy = parseFloat(t.calculatedCubicYards || '0');
        if (cy > 0) map.set(siteId, (map.get(siteId) ?? 0) + cy);
      }
    });
    return map;
  }, [tickets, disposalSites]);

  const filtered = useMemo(() => {
    let list = sitesWithDDMS;
    if (statusFilter && statusFilter !== 'all') list = list.filter(s => s.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        (s.siteName || '').toLowerCase().includes(q) || (s.address || '').toLowerCase().includes(q) ||
        (s.ddms.depId || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (a.siteName || '').localeCompare(b.siteName || ''));
  }, [sitesWithDDMS, statusFilter, search]);

  const sorted = [...filtered].sort((a, b) => {
    if (!sortField) return 0;
    const va = (a as any)[sortField] ?? '';
    const vb = (b as any)[sortField] ?? '';
    const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? n : -n;
  });

  const setField = (key: keyof DebrisDisposalSite, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setFormTypes([]); setFormDDMS({}); setDepSectionOpen(false); setDirFacilityPick(null); setModalOpen(true); };
  const openEdit = (s: DebrisDisposalSite) => {
    setEditTarget(s);
    const { notes, ddms } = decodeDDMSMeta(s.notes);
    setForm({ ...s, notes });
    try { setFormTypes(JSON.parse(s.acceptedTypes || '[]')); } catch { setFormTypes([]); }
    setFormDDMS(ddms);
    setDepSectionOpen(Object.keys(ddms).length > 0);
    setDirFacilityPick(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.siteName?.trim()) {
      notifications.show({ message: 'Site name is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const notesWithMeta = encodeDDMSMeta(form.notes || '', formDDMS);
      const payload = { ...form, incidentId, acceptedTypes: JSON.stringify(formTypes), notes: notesWithMeta };
      if (editTarget) {
        await updateDisposalSite(editTarget.id, payload);
        notifications.show({ message: 'Site updated', color: 'var(--color-success)' });
      } else {
        await createDisposalSite(payload);
        notifications.show({ message: 'Site created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteDisposalSite(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.siteName} deleted`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Disposal Sites</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingSites} onClick={() => loadDisposalSites()}>Refresh</Button>
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
            onClick={() => {
              const headers = ['Site Name', 'Site Type', 'Status', 'Address', 'Capacity CY', 'Current Volume', 'Monitor Name'];
              const rows = sorted.map(s => [
                s.siteName || '',
                SITE_TYPES.find(st => st.value === s.siteType)?.label ?? s.siteType ?? '',
                s.status || '',
                s.address || '',
                s.capacityCubicYards || '',
                s.currentVolume || '',
                s.monitorName || '',
              ]);
              exportCSV('disposal-sites.csv', headers, rows);
            }}>Export CSV</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Site</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search site name, address..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} site{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder style={{ borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                {[
                  { label: 'Site Name', field: 'siteName', align: 'left' as const },
                  { label: 'Type', field: 'siteType', align: 'left' as const },
                  { label: 'DEP ID', field: '', align: 'left' as const },
                  { label: 'Address', field: 'address', align: 'left' as const },
                  { label: 'Status', field: 'status', align: 'left' as const },
                  { label: 'Capacity', field: 'capacityCubicYards', align: 'left' as const },
                  { label: 'Monitor', field: 'monitorName', align: 'left' as const },
                  { label: 'Actions', field: '', align: 'right' as const },
                ].map(col => (
                  <th
                    key={col.label}
                    onClick={() => col.field && toggleSort(col.field)}
                    style={{
                      padding: '10px 16px',
                      textAlign: col.align,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: sortField === col.field && col.field ? '#4B3FFF' : '#64748b',
                      whiteSpace: 'nowrap',
                      cursor: col.field ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}>
                    {col.label}
                    {col.field && (
                      <span style={{ marginLeft: 4, opacity: sortField === col.field ? 1 : 0.25 }}>
                        {sortField === col.field && sortDir === 'desc' ? '↓' : '↑'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
                  {loadingSites ? 'Loading…' : 'No disposal sites found'}
                </td></tr>
              )}
              {sorted.map(s => {
                const cap = parseFloat(s.capacityCubicYards || '0');
                const computedVol = computedVolumeMap.get(s.id);
                const vol = computedVol !== undefined ? computedVol : parseFloat(s.currentVolume || '0');
                const pct = cap > 0 ? Math.min((vol / cap) * 100, 100) : 0;
                const threshold = s.ddms.threshold ?? 80;
                const overThreshold = cap > 0 && pct >= threshold;
                const barColor = overThreshold ? 'var(--color-error)' : pct >= 70 ? '#F86A0B' : '#22c55e';
                const typeShort = SITE_TYPE_SHORT[s.siteType ?? ''] ?? s.siteType ?? '—';
                return (
                  <tr key={s.id}
                    onMouseEnter={() => setHoverRow(s.id)}
                    onMouseLeave={() => setHoverRow(null)}
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: hoverRow === s.id ? '#f8fafc' : '#fff', transition: 'background 0.1s' }}
                    onClick={() => openEdit(s)}>
                    <td style={{ padding: '10px 16px', fontWeight: 600, color: '#1e293b' }}>{s.siteName || '—'}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <Badge size="sm" variant="light" color="#6366f1" style={{ fontWeight: 600, letterSpacing: 0 }}>
                        {typeShort}
                      </Badge>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      {s.ddms.depId
                        ? <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#475569', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{s.ddms.depId}</span>
                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 16px', color: '#475569', maxWidth: 200 }}>{s.address || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <Badge size="sm" color={STATUS_COLORS[s.status] ?? '#939598'} style={{ textTransform: 'capitalize' }}>{s.status}</Badge>
                    </td>
                    <td style={{ padding: '10px 16px', minWidth: 200 }}>
                      {cap > 0 ? (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <div style={{ flex: 1, height: 5, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 99, transition: 'width 0.3s' }} />
                            </div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: overThreshold ? 'var(--color-error)' : '#374151', minWidth: 32, textAlign: 'right' }}>{pct.toFixed(0)}%</span>
                            {overThreshold && <Badge size="xs" color="var(--color-error)" variant="light">Alert</Badge>}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                            {vol.toLocaleString(undefined, { maximumFractionDigits: 0 })} / {cap.toLocaleString()} CY
                          </div>
                        </div>
                      ) : vol > 0 ? (
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{vol.toLocaleString(undefined, { maximumFractionDigits: 0 })} CY</div>
                      ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 16px', color: '#374151' }}>
                      {s.monitorName || <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <Group gap={4} justify="flex-end">
                        {canEdit && <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(s)}>
                          <IconPencil size={14} />
                        </ActionIcon>}
                        {canEdit && <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(s)}>
                          <IconTrash size={14} />
                        </ActionIcon>}
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Disposal Site' : 'Add Disposal Site'} centered size="lg">
        <Stack>
          {dirFacilities.length > 0 && (
            <Select
              label="Pre-fill from Directory facility"
              description="Selecting a facility auto-fills name, address, and coordinates"
              placeholder="Search directory facilities…"
              data={dirFacilities}
              value={dirFacilityPick}
              searchable
              clearable
              onChange={v => {
                setDirFacilityPick(v);
                if (!v) return;
                const fac = dirFacilities.find(f => f.value === v);
                if (!fac) return;
                const addr = [fac.street, fac.city, fac.state, fac.zip].filter(Boolean).join(', ');
                setForm(prev => ({ ...prev, siteName: fac.label, address: addr, latitude: fac.lat, longitude: fac.lng }));
              }}
            />
          )}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Site Name" required value={form.siteName ?? ''}
              onChange={e => setField('siteName', e.target.value)} />
            <Select label="Site Type" data={SITE_TYPES} value={form.siteType || null}
              onChange={v => setField('siteType', v ?? '')} clearable />
            <Autocomplete label="Address" value={form.address ?? ''}
              onChange={handleAddrSearch}
              onOptionSubmit={val => {
                setField('address', val);
                const match = addrSuggestions.find(s => s.value === val);
                if (match) { setField('latitude', match.lat); setField('longitude', match.lng); }
              }}
              data={addrSuggestions.map(s => s.value)}
              placeholder="Start typing an address..." />
            <Select label="Status" data={STATUS_OPTIONS}
              value={(form.status as SiteStatus) ?? 'open'}
              onChange={v => setField('status', v ?? 'open')} />
            <TextInput label="Latitude" value={form.latitude ?? ''}
              onChange={e => setField('latitude', e.target.value)} />
            <TextInput label="Longitude" value={form.longitude ?? ''}
              onChange={e => setField('longitude', e.target.value)} />
            <TextInput label="Capacity (CY)" value={form.capacityCubicYards ?? ''}
              onChange={e => setField('capacityCubicYards', e.target.value)} />
            <TextInput label="Current Volume (CY)" value={form.currentVolume ?? ''}
              onChange={e => setField('currentVolume', e.target.value)} />
            <div>
              <Text size="sm" fw={500} mb={4}>Operating Hours</Text>
              <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                {form.operatingHours || 'Not set'} (not saved — no API column)
              </Text>
            </div>
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Tower Monitor</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Monitor Name" data={monitorSelectData} value={form.monitorName || null}
              onChange={v => {
                setField('monitorName', v ?? '');
                const m = knownMonitors.find(m => m.name === v);
                if (m) setField('monitorEmail', m.email);
              }}
              searchable clearable allowDeselect
              nothingFoundMessage="Type a new monitor name above"
            />
            <TextInput label="Monitor Email" value={form.monitorEmail ?? ''}
              onChange={e => setField('monitorEmail', e.target.value)} />
          </SimpleGrid>

          <MultiSelect label="Accepted Debris Types" data={DEBRIS_TYPE_OPTIONS}
            value={formTypes} onChange={setFormTypes} searchable />

          {/* ── DEP Regulatory Info (collapsible) ─── */}
          <div
            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => setDepSectionOpen(o => !o)}>
            {depSectionOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            <Text size="sm" fw={600} c="dimmed">DEP Regulatory Info</Text>
            {formDDMS.depId && <Badge size="xs" variant="light" color="var(--color-success)">Configured</Badge>}
          </div>
          {depSectionOpen && (
            <Paper p="sm" radius="sm" withBorder style={{ background: '#fafbfc' }}>
              <Stack gap="sm">
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <TextInput label="DEP Authorization ID" placeholder="e.g. FLD-0001234"
                    value={formDDMS.depId ?? ''}
                    onChange={e => setFormDDMS(prev => ({ ...prev, depId: e.target.value }))} />
                  <TextInput label="DEP Contact Name"
                    value={formDDMS.depContactName ?? ''}
                    onChange={e => setFormDDMS(prev => ({ ...prev, depContactName: e.target.value }))} />
                  <TextInput label="DEP Contact Email" type="email"
                    value={formDDMS.depContactEmail ?? ''}
                    onChange={e => setFormDDMS(prev => ({ ...prev, depContactEmail: e.target.value }))} />
                  <TextInput label="DEP Contact Phone"
                    value={formDDMS.depContactPhone ?? ''}
                    onChange={e => setFormDDMS(prev => ({ ...prev, depContactPhone: e.target.value }))} />
                </SimpleGrid>
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                  <Switch
                    label="Putrescible 48-hr Rule"
                    description="Must process putrescible debris within 48 hours"
                    checked={formDDMS.putrescibleRule === true}
                    onChange={e => setFormDDMS(prev => ({ ...prev, putrescibleRule: e.currentTarget.checked }))} />
                  <Switch
                    label="Spotter Required"
                    description="DEP requires an on-site spotter"
                    checked={formDDMS.spotterRequired === true}
                    onChange={e => setFormDDMS(prev => ({ ...prev, spotterRequired: e.currentTarget.checked }))} />
                  <TextInput label="Capacity Alert Threshold (%)" type="number" min={1} max={100}
                    placeholder="80"
                    value={formDDMS.threshold != null ? String(formDDMS.threshold) : ''}
                    onChange={e => setFormDDMS(prev => ({ ...prev, threshold: e.target.value ? parseInt(e.target.value) : undefined }))} />
                </SimpleGrid>
              </Stack>
            </Paper>
          )}

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Site'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Site" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.siteName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
